import { nextAfter } from "./double-bits.js";
//cyCodeBase by Cem Yuksel
//[https://www.cemyuksel.com/research/polynomials/polynomial_roots_hpg2022.pdf]
//[https://www.cemyuksel.com/research/polynomials/]
//[https://github.com/cemyuksel/cyCodeBase/blob/master/cyPolynomial.h]
//-------------------------------------------------------------------------------
//! \file   cyPolynomial.h
//! \author Cem Yuksel
//!
//! \brief  Polynomial operations and solver class and functions.
//!
//! This file includes the implementation of the numerical polynomial solver
//! described in
//!
//! Cem Yuksel. 2022. High-Performance Polynomial Root Finding for Graphics.
//! Proc. ACM Comput. Graph. Interact. Tech. 5, 3, Article 7 (July 2022), 15 pages.
//!
//! http://www.cemyuksel.com/?x=polynomials
//!
//! The polynomial functions take an array of polynomial coefficients in the
//! order of increasing degrees.
//!
//-------------------------------------------------------------------------------
//
//Copyright (c) 2022, Cem Yuksel <cem@cemyuksel.com>
//All rights reserved.
//
//Permission is hereby granted, free of charge, to any person obtaining a copy
//of this software and associated documentation files (the "Software"), to deal
//in the Software without restriction, including without limitation the rights
//to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
//copies of the Software, and to permit persons to whom the Software is
//furnished to do so, subject to the following conditions:
//
//The above copyright notice and this permission notice shall be included in all
//copies or substantial portions of the Software.
//
//THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
//IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
//FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
//AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
//LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
//OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
//SOFTWARE.
//
//-------------------------------------------------------------------------------
export class Polynomial {
    static DEFAULT_ERROR = 6e-7;
    static rootsOpen(coef, xError = Polynomial.DEFAULT_ERROR) {
        const N = coef.length - 1;
        const _roots = array(N + 1);
        const numberOfRootsFound = Polynomial.polynomialRootsOpen(N, false, _roots, coef, xError);
        const roots = array(numberOfRootsFound);
        for (let i = 0; i < numberOfRootsFound; i++) {
            roots[i] = _roots[i];
        }
        return roots;
    }
    static rootsClosed(coef, x0, x1, xError = Polynomial.DEFAULT_ERROR) {
        const N = coef.length - 1;
        const _roots = array(N + 1);
        const numberOfRootsFound = Polynomial.polynomialRootsClosed(N, false, _roots, coef, x0, x1, xError);
        const roots = array(numberOfRootsFound);
        for (let i = 0; i < numberOfRootsFound; i++) {
            roots[i] = _roots[i];
        }
        return roots;
    }
    static linearRootOpen(roots, coef, xError, rootIndex = 0) {
        if (!eqZero(coef[1], xError)) {
            roots[rootIndex] = -coef[0] / coef[1];
            return 1;
        }
        //TODO, if coef[0] == 0 then we have infinite number of roots
        return eqZero(coef[0], xError) ? 1 : 0;
    }
    static linearRootClosed(roots, coef, x0, x1, xError, rootIndex = 0) {
        if (!eqZero(coef[1], xError)) {
            const r = -coef[0] / coef[1];
            if (r >= x0 && r <= x1) {
                roots[rootIndex] = r;
                return 1;
            }
            else {
                return 0;
            }
        }
        else {
            roots[rootIndex] = (x0 + x1) / 2.0;
            //TODO, if coef[0] == 0 then we have infinite number of roots
            return eqZero(coef[0], xError) ? 1 : 0;
        }
    }
    static quadraticRootsOpen(roots, coef, xError, rootIndex = 0) {
        if (!eqZero(coef[2], xError)) {
            const c = coef[0];
            const b = coef[1];
            const a = coef[2];
            const delta = b * b - 4.0 * a * c;
            if (delta > 0.0) {
                const d = Math.sqrt(delta);
                const q = -0.5 * (b + multSign(d, b));
                const rv0 = q / a;
                const rv1 = c / q;
                roots[rootIndex] = Math.min(rv0, rv1);
                roots[rootIndex + 1] = Math.max(rv0, rv1);
                return 2;
            }
            else if (delta < 0)
                return 0;
            roots[rootIndex] = (-0.5 * b) / a;
            return a != 0.0 ? 1 : 0;
        }
        else {
            return Polynomial.linearRootOpen(roots, coef, xError, rootIndex);
        }
    }
    static quadraticRootsClosed(roots, coef, x0, x1, xError, rootIndex = 0) {
        if (!eqZero(coef[2], xError)) {
            const c = coef[0];
            const b = coef[1];
            const a = coef[2];
            const delta = b * b - 4.0 * a * c;
            if (delta > 0.0) {
                const d = Math.sqrt(delta);
                const q = -0.5 * (b + multSign(d, b));
                const rv0 = q / a;
                const rv1 = c / q;
                const r0 = Math.min(rv0, rv1);
                const r1 = Math.max(rv0, rv1);
                const r0i = r0 >= x0 && r0 <= x1 ? 1 : 0;
                const r1i = r1 >= x0 && r1 <= x1 ? 1 : 0;
                roots[rootIndex] = r0;
                roots[rootIndex + r0i] = r1;
                return r0i + r1i;
            }
            else if (delta < 0)
                return 0;
            const r0 = (-0.5 * b) / a;
            const r0i = r0 >= x0 && r0 <= x1 ? 1 : 0;
            if (r0i > 0)
                roots[rootIndex] = r0;
            return r0i;
        }
        else {
            return Polynomial.linearRootClosed(roots, coef, x0, x1, xError, rootIndex);
        }
    }
    static cubicRootsOpen(boundError, roots, coef, xError) {
        if (!eqZero(coef[3], xError)) {
            const a = coef[3] * 3.0;
            const b_2 = coef[2];
            const c = coef[1];
            const deriv = [c, 2.0 * b_2, a, 0.0];
            const delta_4 = b_2 * b_2 - a * c;
            if (delta_4 > 0.0) {
                const d_2 = Math.sqrt(delta_4);
                const q = -(b_2 + multSign(d_2, b_2));
                const rv0 = q / a;
                const rv1 = c / q;
                const xa = Math.min(rv0, rv1);
                const xb = Math.max(rv0, rv1);
                const ya = Polynomial.evaluate(3, coef, xa);
                const yb = Polynomial.evaluate(3, coef, xb);
                if (!isDifferentSign(coef[3], ya)) {
                    roots[0] = RootFinderNewton.findOpenMin(3, boundError, coef, deriv, xa, ya, xError);
                    if (!boundError) {
                        if (isDifferentSign(ya, yb)) {
                            const defPoly = array(4);
                            Polynomial.deflate(3, defPoly, coef, roots[0]);
                            return Polynomial.quadraticRootsOpen(roots, defPoly, xError, 1) + 1;
                        }
                    }
                    else {
                        if (isDifferentSign(ya, yb)) {
                            roots[1] = RootFinderNewton.findClosed(3, boundError, coef, deriv, xa, xb, ya, yb, xError);
                            roots[2] = RootFinderNewton.findOpenMax(3, boundError, coef, deriv, xb, yb, xError);
                            return 3;
                        }
                    }
                }
                else {
                    roots[0] = RootFinderNewton.findOpenMax(3, boundError, coef, deriv, xb, yb, xError);
                }
                return 1;
            }
            else {
                const x_inf = -b_2 / a;
                const y_inf = Polynomial.evaluate(3, coef, x_inf);
                if (isDifferentSign(coef[3], y_inf)) {
                    roots[0] = RootFinderNewton.findOpenMax(3, boundError, coef, deriv, x_inf, y_inf, xError);
                }
                else {
                    roots[0] = RootFinderNewton.findOpenMin(3, boundError, coef, deriv, x_inf, y_inf, xError);
                }
                return 1;
            }
        }
        else {
            return Polynomial.quadraticRootsOpen(roots, coef, xError);
        }
    }
    static cubicRootsClosed(boundError, roots, coef, x0, x1, xError) {
        if (!eqZero(coef[3], xError)) {
            const y0 = Polynomial.evaluate(3, coef, x0);
            const y1 = Polynomial.evaluate(3, coef, x1);
            const a = coef[3] * 3.0;
            const b_2 = coef[2];
            const c = coef[1];
            const deriv = [c, 2.0 * b_2, a, 0.0];
            const delta_4 = b_2 * b_2 - a * c;
            if (delta_4 > 0) {
                const d_2 = Math.sqrt(delta_4);
                const q = -(b_2 + multSign(d_2, b_2));
                const rv0 = q / a;
                const rv1 = c / q;
                const xa = Math.min(rv0, rv1);
                const xb = Math.max(rv0, rv1);
                if (isDifferentSign(y0, y1)) {
                    if (xa >= x1 || xb <= x0 || (xa <= x0 && xb >= x1)) {
                        // first, last, or middle interval only
                        roots[0] = RootFinderNewton.findClosed(3, boundError, coef, deriv, x0, x1, y0, y1, xError);
                        return 1;
                    }
                }
                else {
                    if (xa >= x1 || xb <= x0 || (xa <= x0 && xb >= x1))
                        return 0;
                }
                let numRoots = 0;
                if (xa > x0) {
                    const ya = Polynomial.evaluate(3, coef, xa);
                    if (isDifferentSign(y0, ya)) {
                        roots[0] = RootFinderNewton.findClosed(3, boundError, coef, deriv, x0, xa, y0, ya, xError); // first
                        // interval
                        if (!boundError) {
                            if (isDifferentSign(ya, y1) ||
                                (xb < x1 &&
                                    isDifferentSign(ya, Polynomial.evaluate(3, coef, xb)))) {
                                const defPoly = array(4);
                                Polynomial.deflate(3, defPoly, coef, roots[0]);
                                return (Polynomial.quadraticRootsClosed(roots, defPoly, xa, x1, xError, 1) + 1);
                            }
                            else
                                return 1;
                        }
                        else
                            numRoots++;
                    }
                    if (xb < x1) {
                        const yb = Polynomial.evaluate(3, coef, xb);
                        if (isDifferentSign(ya, yb)) {
                            roots[!boundError ? 0 : numRoots++] = RootFinderNewton.findClosed(3, boundError, coef, deriv, xa, xb, ya, yb, xError);
                            if (!boundError) {
                                if (isDifferentSign(yb, y1)) {
                                    const defPoly = array(4);
                                    Polynomial.deflate(3, defPoly, coef, roots[0]);
                                    return (Polynomial.quadraticRootsClosed(roots, defPoly, xb, x1, xError, 1) + 1);
                                }
                                else
                                    return 1;
                            }
                        }
                        if (isDifferentSign(yb, y1)) {
                            roots[!boundError ? 0 : numRoots++] = RootFinderNewton.findClosed(3, boundError, coef, deriv, xb, x1, yb, y1, xError); // last interval
                            if (!boundError)
                                return 1;
                        }
                    }
                    else {
                        if (isDifferentSign(ya, y1)) {
                            roots[!boundError ? 0 : numRoots++] = RootFinderNewton.findClosed(3, boundError, coef, deriv, xa, x1, ya, y1, xError);
                            if (!boundError)
                                return 1;
                        }
                    }
                }
                else {
                    const yb = Polynomial.evaluate(3, coef, xb);
                    if (isDifferentSign(y0, yb)) {
                        roots[0] = RootFinderNewton.findClosed(3, boundError, coef, deriv, x0, xb, y0, yb, xError);
                        if (!boundError) {
                            if (isDifferentSign(yb, y1)) {
                                const defPoly = array(4);
                                Polynomial.deflate(3, defPoly, coef, roots[0]);
                                return (Polynomial.quadraticRootsClosed(roots, defPoly, xb, x1, xError, 1) + 1);
                            }
                            else
                                return 1;
                        }
                        else
                            numRoots++;
                    }
                    if (isDifferentSign(yb, y1)) {
                        roots[!boundError ? 0 : numRoots++] = RootFinderNewton.findClosed(3, boundError, coef, deriv, xb, x1, yb, y1, xError); // last interval
                        if (!boundError)
                            return 1;
                    }
                }
                return numRoots;
            }
            else {
                if (isDifferentSign(y0, y1)) {
                    roots[0] = RootFinderNewton.findClosed(3, boundError, coef, deriv, x0, x1, y0, y1, xError);
                    return 1;
                }
                return 0;
            }
        }
        else {
            return Polynomial.quadraticRootsClosed(roots, coef, x0, x1, xError);
        }
    }
    static polynomialRootsOpen(N, boundError, roots, coef, xError) {
        if (N == 1)
            return Polynomial.linearRootOpen(roots, coef, xError);
        else if (N == 2)
            return Polynomial.quadraticRootsOpen(roots, coef, xError);
        else if (N == 3)
            return Polynomial.cubicRootsOpen(boundError, roots, coef, xError);
        else if (eqZero(coef[N], xError))
            return Polynomial.polynomialRootsOpen(N - 1, boundError, roots, coef, xError);
        else {
            const deriv = array(N);
            Polynomial.derivative(N, deriv, coef);
            const derivRoots = array(N - 1);
            const nd = Polynomial.polynomialRootsOpen(N - 1, boundError, derivRoots, deriv, xError);
            if ((N & 1) == 1 || ((N & 1) == 0 && nd > 0)) {
                let nr = 0;
                let xa = derivRoots[0];
                let ya = Polynomial.evaluate(N, coef, xa);
                if (isDifferentSign(coef[N], ya) != ((N & 1) != 0)) {
                    roots[0] = RootFinderNewton.findOpenMin(N, boundError, coef, deriv, xa, ya, xError);
                    nr = 1;
                }
                for (let i = 1; i < nd; ++i) {
                    const xb = derivRoots[i];
                    const yb = Polynomial.evaluate(N, coef, xb);
                    if (isDifferentSign(ya, yb)) {
                        roots[nr++] = RootFinderNewton.findClosed(N, boundError, coef, deriv, xa, xb, ya, yb, xError);
                    }
                    xa = xb;
                    ya = yb;
                }
                if (isDifferentSign(coef[N], ya)) {
                    roots[nr++] = RootFinderNewton.findOpenMax(N, boundError, coef, deriv, xa, ya, xError);
                }
                return nr;
            }
            else {
                if ((N & 1) == 1) {
                    roots[0] = RootFinderNewton.findOpen(N, boundError, coef, deriv, xError);
                    return 1;
                }
                else
                    return 0; // this should not happen
            }
        }
    }
    static polynomialRootsClosed(N, boundError, roots, coef, x0, x1, xError) {
        if (N == 1)
            return Polynomial.linearRootClosed(roots, coef, x0, x1, xError);
        else if (N == 2)
            return Polynomial.quadraticRootsClosed(roots, coef, x0, x1, xError);
        else if (N == 3)
            return Polynomial.cubicRootsClosed(boundError, roots, coef, x0, x1, xError);
        else if (eqZero(coef[N], xError))
            return Polynomial.polynomialRootsClosed(N - 1, boundError, roots, coef, x0, x1, xError);
        else {
            const y0 = Polynomial.evaluate(N, coef, x0);
            const deriv = array(N);
            Polynomial.derivative(N, deriv, coef);
            const derivRoots = array(N - 1);
            const nd = Polynomial.polynomialRootsClosed(N - 1, boundError, derivRoots, deriv, x0, x1, xError);
            const x = array(N + 1);
            x[0] = x0;
            const y = array(N + 1);
            x[0] = y0;
            for (let i = 0; i < nd; ++i) {
                x[i + 1] = derivRoots[i];
                y[i + 1] = Polynomial.evaluate(N, coef, derivRoots[i]);
            }
            x[nd + 1] = x1;
            y[nd + 1] = Polynomial.evaluate(N, coef, x1);
            let nr = 0;
            for (let i = 0; i <= nd; ++i) {
                if (isDifferentSign(y[i], y[i + 1])) {
                    roots[nr++] = RootFinderNewton.findClosed(N, boundError, coef, deriv, x[i], x[i + 1], y[i], y[i + 1], xError);
                }
            }
            return nr;
        }
    }
    /**
     * Evaluates the given polynomial of degree `N` at `x`.
     *
     * The coefficients in the `coef` array must be in the order of increasing
     * degrees.
     * @param N
     * @param coef
     * @param x
     * @returns
     */
    static evaluate(N, coef, x) {
        let r = coef[N];
        for (let i = N - 1; i >= 0; --i)
            r = r * x + coef[i];
        return r;
    }
    /**
     *  Computes the polynomial's derivative and sets the coefficients of the
     * `deriv` array.
     *
     * Note that a degree N polynomial's derivative is degree `N-1` and has `N`
     * coefficients.
     * The coefficients are in the order of increasing degrees.
     *
     * @param N
     * @param deriv
     * @param coef
     */
    static derivative(N, deriv, coef) {
        deriv[0] = coef[1];
        for (let i = 2; i <= N; ++i) {
            deriv[i - 1] = i * coef[i];
        }
    }
    /**
     * Deflates the given polynomial using one of its known roots.
     *
     * Stores the coefficients of the deflated polynomial in the `defPoly` array.
     * Let f(x) represent the given polynomial.
     * This computes the deflated polynomial g(x) of a lower degree such that
     *
     * `f(x) = (x - root) * g(x)`.
     *
     * The given root must be a valid root of the given polynomial.
     * Note that the deflated polynomial has degree `N-1` with `N` coefficients.
     * The coefficients are in the order of increasing degrees.
     *
     * @param N
     * @param defPoly
     * @param coef
     * @param root
     */
    static deflate(N, defPoly, coef, root) {
        defPoly[N - 1] = coef[N];
        for (let i = N - 1; i > 0; --i) {
            defPoly[i - 1] = coef[i] + root * defPoly[i];
        }
    }
    /**
     * Inflates the given polynomial using the given root.
     *
     * Stores the coefficients of the inflated polynomial in the `infPoly` array.
     * Let f(x) represent the given polynomial.
     * This computes the inflated polynomial g(x) of a higher degree such that
     *
     * `g(x) = (x - root) * f(x)`.
     *
     * Note that the inflated polynomial has degree `N+1` with `N+2` coefficients.
     * The coefficients are in the order of increasing degrees.
     *
     * @param N
     * @param infPoly
     * @param coef
     * @param root
     */
    static inflate(N, infPoly, coef, root) {
        infPoly[N + 1] = coef[N];
        for (let i = N - 1; i >= 0; --i) {
            infPoly[i + 1] = coef[i] - root * infPoly[i + 1];
        }
        infPoly[0] = -root * coef[0];
    }
}
/**
 * Returns true when abs(v) < xError
 * @param v the value to test
 * @param tolerance the allowed tolerance
 * @return true when v closer to zero then allowed tolerance
 */
const eqZero = (v, tolerance) => {
    return Math.abs(v) < tolerance;
};
/**
 * Multiplies the given value with the given sign
 * @param v
 * @param sign
 * @returns
 */
const multSign = (v, sign) => {
    return v * (sign < 0.0 ? -1.0 : 1.0);
};
/**
 * Returns true if the sign bits are different
 * @param a
 * @param b
 * @returns
 */
const isDifferentSign = (a, b) => {
    return a < 0.0 != b < 0.0;
};
/**
 * Returns a number whose value is limited to the given range.
 *
 * Example: limit the output of this computation to between 0 and 255
 * (x * 255).clamp(0, 255)
 *
 * @param min The lower boundary of the output range
 * @param max The upper boundary of the output range
 * @returns A number in the range [min, max]
 */
const clamp = (value, min, max) => {
    return Math.min(Math.max(value, min), max);
};
/**
 * Creates an array of given length filled with given value
 * @param length the length of the array
 * @param value the initial value of each item in the array, defaults to 0.0
 * @returns  the created array
 */
const array = (length, value = 0.0) => {
    const a = new Array(length);
    for (let i = 0; i < length; i++) {
        a[i] = value;
    }
    return a;
};
class RootFinderNewton {
    /**
     * Finds the single root within a closed interval between `x0` and `x1`.
     *
     * It combines Newton iterations with bisection to ensure convergence.
     * It takes a polynomial's coefficients `coef` and its derivative's
     * coefficients `deriv` along with the x bounds `x0` and `x1`
     * and the values of the polynomial `y0` and `y1` computed at `x0` and `x1`
     * respectively.
     * It almost always satisfies the given error bound `xError` but this is not
     * guaranteed unless `boundError` is set to `true`.
     * If `boundError` is `true`, it performs additional operations to bound the
     * error.
     *
     * @param N
     * @param boundError
     * @param coef
     * @param deriv
     * @param x0
     * @param x1
     * @param y0
     * @param y1
     * @param xError
     * @returns
     */
    static findClosed(N, boundError, coef, deriv, x0, x1, y0, y1, xError) {
        const ep2 = 2.0 * xError;
        let xr = (x0 + x1) / 2; // mid point
        if (x1 - x0 <= ep2)
            return xr;
        if (N <= 3) {
            const xr0 = xr;
            for (let safetyCounter = 0; safetyCounter < 16; ++safetyCounter) {
                let xn = xr -
                    Polynomial.evaluate(N, coef, xr) / Polynomial.evaluate(2, deriv, xr);
                xn = clamp(xn, x0, x1);
                if (Math.abs(xr - xn) <= xError)
                    return xn;
                xr = xn;
            }
            if (!Number.isFinite(xr))
                xr = xr0;
        }
        let yr = Polynomial.evaluate(N, coef, xr);
        let xb0 = x0;
        let xb1 = x1;
        while (true) {
            const side = isDifferentSign(y0, yr) ? 1 : 0;
            if (side != 0)
                xb1 = xr;
            else
                xb0 = xr;
            const dy = Polynomial.evaluate(N - 1, deriv, xr);
            const dx = yr / dy;
            const xn = xr - dx;
            if (xn > xb0 && xn < xb1) {
                // valid Newton step
                const stepsize = Math.abs(xr - xn);
                xr = xn;
                if (stepsize > xError) {
                    yr = Polynomial.evaluate(N, coef, xr);
                }
                else {
                    if (boundError) {
                        let xs;
                        if (xError == 0.0) {
                            xs = nextAfter(side != 0 ? xb1 : xb0, side != 0 ? xb0 : xb1);
                        }
                        else {
                            xs = xn - multSign(xError, side - 1);
                            if (xs == xn)
                                xs = nextAfter(side != 1 ? xb1 : xb0, side != 1 ? xb0 : xb1);
                        }
                        const ys = Polynomial.evaluate(N, coef, xs);
                        const s = isDifferentSign(y0, ys) ? 1 : 0;
                        if (side != s)
                            return xn;
                        xr = xs;
                        yr = ys;
                    }
                    else
                        break;
                }
            }
            else {
                // Newton step failed
                xr = (xb0 + xb1) / 2.0;
                if (xr == xb0 || xr == xb1 || xb1 - xb0 <= ep2) {
                    if (boundError) {
                        if (xError == 0.0) {
                            const xm = side != 0 ? xb0 : xb1;
                            const ym = Polynomial.evaluate(N, coef, xm);
                            if (Math.abs(ym) < Math.abs(yr))
                                xr = xm;
                        }
                    }
                    break;
                }
                yr = Polynomial.evaluate(N, coef, xr);
            }
        }
        return xr;
    }
    /**
     * Finds the single root in an infinite interval.
     *
     * This is intended for root finding in an open interval for polynomials with
     * odd degrees.
     * Polynomials with odd degrees always have at least one real root.
     * If that is the only root, this function can be used to find it.
     *
     * @param N
     * @param boundError
     * @param coef
     * @param deriv
     * @param xError
     * @returns
     */
    static findOpen(N, boundError, coef, deriv, xError) {
        if ((N & 1) == 0)
            throw new Error("FindOpen only works for polynomials with odd degrees.");
        const xr = 0.0;
        const yr = coef[0]; // PolynomialEval<N,ftype>( coef, xr );
        if (isDifferentSign(coef[N], yr)) {
            return RootFinderNewton.findOpenMax(N, boundError, coef, deriv, xr, yr, xError);
        }
        else {
            return RootFinderNewton.findOpenMin(N, boundError, coef, deriv, xr, yr, xError);
        }
    }
    /**
     * Finds the single root from negative infinity to the given x bound `x1`.
     *
     * @param N
     * @param boundError
     * @param coef
     * @param deriv
     * @param x1
     * @param y1
     * @param xError
     * @returns
     */
    static findOpenMin(N, boundError, coef, deriv, x1, y1, xError) {
        return RootFinderNewton._findOpen(N, boundError, true, coef, deriv, x1, y1, x1 - 1.0, xError);
    }
    /**
     * Finds the single root from the given x bound `x0` to positive infinity.
     *
     * @param N
     * @param boundError
     * @param coef
     * @param deriv
     * @param x0
     * @param y0
     * @param xError
     * @returns
     */
    static findOpenMax(N, boundError, coef, deriv, x0, y0, xError) {
        return RootFinderNewton._findOpen(N, boundError, false, coef, deriv, x0, y0, x0 + 1.0, xError);
    }
    /**
     * The implementation of FindOpenMin and FindOpenMax
     * @param N
     * @param boundError
     * @param openMin
     * @param coef
     * @param deriv
     * @param xm
     * @param ym
     * @param xr
     * @param xError
     * @returns
     */
    static _findOpen(N, boundError, openMin, coef, deriv, xm, ym, xr, xError) {
        let delta = 1.0;
        let yr = Polynomial.evaluate(N, coef, xr);
        let otherSide = isDifferentSign(ym, yr);
        while (yr != 0.0) {
            if (otherSide) {
                if (openMin) {
                    return RootFinderNewton.findClosed(N, boundError, coef, deriv, xr, xm, yr, ym, xError);
                }
                else {
                    return RootFinderNewton.findClosed(N, boundError, coef, deriv, xm, xr, ym, yr, xError);
                }
            }
            else {
                //			open_interval:
                let openInterval = true;
                while (openInterval) {
                    openInterval = false;
                    xm = xr;
                    ym = yr;
                    const dy = Polynomial.evaluate(N - 1, deriv, xr);
                    const dx = yr / dy;
                    const xn = xr - dx;
                    const dif = openMin ? xr - xn : xn - xr;
                    if (dif <= 0 && Number.isFinite(xn)) {
                        // valid Newton step
                        xr = xn;
                        if (dif <= xError) {
                            // we might have converged
                            if (xr == xm)
                                break;
                            const xs = xn - multSign(xError, -(openMin ? 1.0 : 0.0));
                            const ys = Polynomial.evaluate(N, coef, xs);
                            const s = isDifferentSign(ym, ys);
                            if (s)
                                break;
                            xr = xs;
                            yr = ys;
                            openInterval = true;
                            // break open_interval;
                        }
                    }
                    else {
                        // Newton step failed
                        xr = openMin ? xr - delta : xr + delta;
                        delta *= 2.0;
                    }
                    if (!openInterval) {
                        yr = Polynomial.evaluate(N, coef, xr);
                        otherSide = isDifferentSign(ym, yr);
                    }
                }
            }
        }
        return xr;
    }
}
